import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, X } from "lucide-react";

/**
 * SearchCombobox — input de recherche + popup déroulant pour sélectionner UN item.
 *
 * Props
 * - items: Array — la liste complète à filtrer
 * - filterFn: (item, query) => boolean — fonction de filtrage
 * - renderItem: (item) => ReactNode — rendu d'une ligne dans la popup
 * - getKey: (item) => string — clé React
 * - getLabel: (item) => string — label affiché quand l'item est sélectionné
 * - selected: item|null — item actuellement sélectionné
 * - onSelect: (item) => void — callback de sélection
 * - placeholder, testId — divers
 */
export const SearchCombobox = ({
  items, filterFn, renderItem, getKey, getLabel,
  selected, onSelect,
  placeholder = "Tapez pour rechercher…",
  testId,
  maxResults = 30,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (selected) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-800 truncate max-w-[420px] py-1">
          {getLabel(selected)}
        </Badge>
        <Button size="sm" variant="ghost" onClick={() => onSelect(null)} data-testid={`${testId}-clear`}>
          <X className="h-3 w-3 mr-1" /> Changer
        </Button>
      </div>
    );
  }

  const filtered = query.trim()
    ? items.filter((i) => filterFn(i, query.trim().toLowerCase())).slice(0, maxResults)
    : [];

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
        <Input
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
          className="pl-8"
          data-testid={testId}
        />
      </div>
      {open && query.trim() && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-zinc-200 rounded-xl shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Aucun résultat</div>
          ) : (
            filtered.map((item) => (
              <button
                key={getKey(item)}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-zinc-100 last:border-b-0 transition-colors"
                onClick={() => { onSelect(item); setQuery(""); setOpen(false); }}
                data-testid={`${testId}-option-${getKey(item)}`}
              >
                {renderItem(item)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * MultiSearchCombobox — input de recherche + popup pour sélectionner PLUSIEURS items.
 * Les sélectionnés s'affichent en chips au-dessus.
 */
export const MultiSearchCombobox = ({
  items, filterFn, renderItem, getKey, getLabel,
  selectedKeys = [], onChange,
  placeholder = "Tapez pour rechercher et ajouter…",
  testId,
  maxResults = 30,
  emptyHint,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const selectedSet = new Set(selectedKeys);
  const selectedItems = items.filter((i) => selectedSet.has(getKey(i)));
  const filtered = query.trim()
    ? items
        .filter((i) => !selectedSet.has(getKey(i)))
        .filter((i) => filterFn(i, query.trim().toLowerCase()))
        .slice(0, maxResults)
    : [];

  const toggle = (item) => {
    const k = getKey(item);
    const next = selectedSet.has(k)
      ? selectedKeys.filter((x) => x !== k)
      : [...selectedKeys, k];
    onChange?.(next);
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-chips`}>
          {selectedItems.map((item) => (
            <Badge
              key={getKey(item)}
              variant="outline"
              className="border-indigo-300 bg-indigo-50 text-indigo-800 py-1 pr-1 max-w-[320px]"
              data-testid={`${testId}-chip-${getKey(item)}`}
            >
              <span className="truncate">{getLabel(item)}</span>
              <button
                type="button"
                onClick={() => toggle(item)}
                className="ml-1.5 hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                aria-label="Retirer"
                data-testid={`${testId}-remove-${getKey(item)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          <Input
            placeholder={placeholder}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            autoComplete="off"
            className="pl-8"
            data-testid={testId}
          />
        </div>
        {open && query.trim() && (
          <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-zinc-200 rounded-xl shadow-lg">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {emptyHint || "Aucun résultat"}
              </div>
            ) : (
              filtered.map((item) => (
                <button
                  key={getKey(item)}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-zinc-100 last:border-b-0 transition-colors"
                  onClick={() => { toggle(item); setQuery(""); }}
                  data-testid={`${testId}-option-${getKey(item)}`}
                >
                  {renderItem(item)}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchCombobox;
